import type { MySkill } from '../../lib/api-client';

interface Props {
  /** Skills the current user is entitled to (already filtered to their grants). */
  availableSkills: MySkill[];
  skillsStatus: 'idle' | 'loading' | 'success' | 'error';
  /** null = unset (all available enabled by default); array = explicit selection. */
  selectedSkillIds: string[] | null;
  onToggleSkill: (skillId: string) => void;
  onSelectAllSkills: () => void;
  onSelectNoneSkills: () => void;
  /** Persist the selection after a change (same path as the composer popover). */
  onSaveSkills: () => void;
  /** Type a starter prompt into the composer (does not send). */
  onPickSuggestion: (text: string) => void;
}

/**
 * Starter prompts shown on a fresh conversation. Clicking one types the text into
 * the composer (it does not auto-send) so the user can edit before sending.
 * Ported from the v1 client's PlaybookSuggestions (payments domain).
 */
const PLAYBOOK_SUGGESTIONS: { icon: string; text: string }[] = [
  {
    icon: '📊',
    text: 'Prepare a quarterly business review deck for my top 10 merchants',
  },
  {
    icon: '📈',
    text: "Break down my portfolio's approval rates by processor and card brand",
  },
  {
    icon: '📉',
    text: 'Find merchants whose processing volume dropped more than 20% this month',
  },
  {
    icon: '🔎',
    text: "Pull a report for yesterday's decline codes",
  },
];

/**
 * Empty state for a conversation with no messages yet. Surfaces two things:
 *   1. An interactive skills card — the SAME role-scoped list as the composer's
 *      🧩 popover, all selected by default, so the user can see (and narrow) what
 *      the agent can do this session.
 *   2. Playbook starter prompts that prefill the composer.
 */
export function ConversationEmptyState({
  availableSkills,
  skillsStatus,
  selectedSkillIds,
  onToggleSkill,
  onSelectAllSkills,
  onSelectNoneSkills,
  onSaveSkills,
  onPickSuggestion,
}: Props) {
  // "all" when unset (null) — every available skill is implicitly enabled.
  const isAll = selectedSkillIds === null;
  const isChecked = (id: string) => (isAll ? true : selectedSkillIds!.includes(id));
  const enabledCount = isAll ? availableSkills.length : selectedSkillIds!.length;

  const toggle = (id: string) => {
    onToggleSkill(id);
    onSaveSkills();
  };
  const selectAll = () => {
    onSelectAllSkills();
    onSaveSkills();
  };
  const selectNone = () => {
    onSelectNoneSkills();
    onSaveSkills();
  };

  const showSkillsCard = availableSkills.length > 0 || skillsStatus === 'loading';

  return (
    <div
      style={{
        maxWidth: 640,
        margin: '0 auto',
        padding: '32px 4px 8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          Let&rsquo;s run your playbook.
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-soft)' }}>
          Pick a starter below, or just type what you need.
        </div>
      </div>

      {showSkillsCard && (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 10,
            background: 'var(--surface)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--text)',
              }}
            >
              <span aria-hidden="true">🧩</span>
              <span>All skills are available this session</span>
              <span style={{ fontWeight: 500, color: 'var(--text-soft)' }}>
                {enabledCount}/{availableSkills.length}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
                <button type="button" onClick={selectAll} style={linkBtn}>
                  Select all
                </button>
                <button type="button" onClick={selectNone} style={linkBtn}>
                  Select none
                </button>
              </span>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-soft)', lineHeight: 1.45 }}>
              Your agent can use every skill below. Narrowing to just the ones this task needs sharpens
              its accuracy — you can change this anytime from the 🧩 icon in the prompt bar.
            </p>
          </div>

          <div style={{ maxHeight: 240, overflowY: 'auto', padding: 4 }}>
            {skillsStatus === 'loading' && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--text-mute)' }}>Loading skills…</div>
            )}
            {skillsStatus === 'error' && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--kind-failure)' }}>
                Failed to load skills.
              </div>
            )}
            {(skillsStatus === 'success' || skillsStatus === 'idle') &&
              availableSkills.map((skill) => {
                const checked = isChecked(skill.skill_id);
                return (
                  <label
                    key={skill.skill_id}
                    role="option"
                    aria-selected={checked}
                    onClick={(e) => {
                      e.preventDefault();
                      toggle(skill.skill_id);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 8,
                      padding: '8px 12px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: checked ? 'var(--accent-soft)' : 'transparent',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ marginTop: 2, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{skill.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.4 }}>
                        {skill.description}
                      </span>
                    </span>
                  </label>
                );
              })}
          </div>
        </div>
      )}

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 10,
            fontSize: 10,
            color: 'var(--text-mute)',
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
          }}
        >
          <span aria-hidden="true">🔀</span>
          <span>Pick a playbook to start</span>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 8,
          }}
        >
          {PLAYBOOK_SUGGESTIONS.map((s, i) => (
            <button
              key={s.text}
              type="button"
              data-testid={`suggestion-${i}`}
              onClick={() => onPickSuggestion(s.text)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '12px 14px',
                borderRadius: 10,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                textAlign: 'left',
                fontSize: 13,
                color: 'var(--text-soft)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                transition: 'border-color 120ms ease, background 120ms ease, color 120ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--accent)';
                e.currentTarget.style.color = 'var(--text)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.color = 'var(--text-soft)';
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                {s.icon}
              </span>
              <span>{s.text}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const linkBtn: React.CSSProperties = {
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  fontSize: 11,
  color: 'var(--text-soft)',
  fontFamily: 'inherit',
  padding: 0,
};
